/**
 * UC-21: EvaluateScenarioObjective
 * TOGAF Training context ABB — checks scenario win conditions without UI deps.
 */
import type { CityStats, ResearchState, ScenarioObjective } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { SimulationEngine } from '../../domain/services/SimulationEngine';

export interface EvaluateScenarioObjectiveInput {
  objectives: ScenarioObjective[];
  stats: CityStats;
  buildingCounts: Record<string, number>;
  research: ResearchState;
  violationCount: number;
}

export interface EvaluateScenarioObjectiveOutput {
  objectives: ScenarioObjective[];
  /** Descriptions of objectives newly marked complete this evaluation */
  newlyCompleted: string[];
}

export interface EvaluateScenarioObjectiveDependencies {
  simulation: Pick<SimulationEngine, 'checkScenarioObjective'>;
  logger?: IAppLogger;
}

export class EvaluateScenarioObjectiveUseCase {
  constructor(private readonly deps: EvaluateScenarioObjectiveDependencies) {}

  execute(input: EvaluateScenarioObjectiveInput): EvaluateScenarioObjectiveOutput {
    const newlyCompleted: string[] = [];

    const objectives = input.objectives.map((obj) => {
      if (obj.completed) return obj;

      const isMet = this.deps.simulation.checkScenarioObjective(
        obj.description,
        input.stats,
        input.buildingCounts,
        input.research,
        input.violationCount,
      );

      if (isMet) {
        newlyCompleted.push(obj.description);
        return { ...obj, completed: true };
      }

      return obj;
    });

    this.deps.logger?.log('debug', 'training.scenario_objectives', {
      total: input.objectives.length,
      newlyCompleted: newlyCompleted.length,
    });

    return { objectives, newlyCompleted };
  }
}